import { Box, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, InputLabel, MenuItem, OutlinedInput, Select, SelectChangeEvent, Stack, Typography } from '@mui/material'
import { FunctionComponent, useEffect, useRef, useState } from 'react'
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { IPostRule, IRule, IRulePreview } from '../../../../shared/services/rule/rule.model';
import RuleService from '../../../../shared/services/rule/rule.service';
import { DialogSaveButtons } from '../../../presentation-components/miscellaneous-components/dialog-save-buttons';
import { IGroupWithCategory } from '../../../../shared/services/group/group.model';
import { sortGroupsByGhgCategory, formatGroupLabel } from '../../../../shared/constants/ghg-category';
import { ICategory } from '../../../../shared/services/category/category.model';
import GroupService from '../../../../shared/services/group/group.service';
import CategoryService from '../../../../shared/services/category/category.service';
import { TooltipButton } from '../../../presentation-components/miscellaneous-components/tooltip-button';
import { EmissionsFilterLogic } from '../../../presentation-components/emissons-components/overview/emissions-filter/emissions-filter-logic';
import ConversionSourceService from '../../../../shared/services/conversion-source/conversion-source.service';
import { IConversionSource } from '../../../../shared/services/conversion-source/conversion-source.model';
import { findCategoryByDisplayName } from '../../../../shared/utils/emission-filter-logic-utils';
import { getCategoryDisplayName } from '../../../../shared/utils/category-utils';
import { useDebounce } from '../../../../hooks/use-debounce-hook';
import { formatRuleFactorExample } from '../../../../shared/utils/rule-impact-utils';

const VEHICLE_TYPES = ['Passenger vehicle', 'Delivery vehicle'];
const NO_FACTORS_MESSAGE = 'No conversion factors were found for this category and source combination.';
const FACTOR_SELECTION_FIELDS = ['detailLevelOne', 'detailLevelTwo', 'detailLevelThree', 'detailLevelFour', 'detailLevelFive', 'measurementUnitName'] as const;

export const RuleDialog: FunctionComponent<{
  ruleId?: string,
  open: boolean,
  closeHandler: () => void,
  isEdit: boolean,
  isSubmitting: boolean,
  onSubmit: (values: IPostRule, restForm: () => void, isEdit: boolean) => void,
  errorCode?: number,
  errorMessage?: string,
  // Where to load an existing rule from. Defaults to the client's own rules; the partner
  // Library passes its own loader so this dialog can author library rules too. Creating and
  // saving are already delegated to the caller via onSubmit, so this fetch was the only
  // thing tying the dialog to one endpoint.
  fetchRule?: (id: string) => Promise<IRule>,
  // Noun used in the title, e.g. 'Library Rule' -> "Add Library Rule".
  titleNoun?: string,
}> = ({
  ruleId,
  open,
  closeHandler,
  isEdit,
  onSubmit,
  isSubmitting,
  errorCode,
  errorMessage,
  fetchRule,
  titleNoun = 'Rule',
}) => {
    //Services
    const ruleService = new RuleService()
    const groupService = new GroupService()
    const categoryService = new CategoryService()
    const conversionSourceService = new ConversionSourceService();
    //State
    const [rule, setRule] = useState<IRule>()
    const [conversionSourceId, setConversionSourceId] = useState<string>()
    const [groups, setGroups] = useState<IGroupWithCategory[]>()
    const [groupId, setGroupId] = useState<string>()
    const [categories, setCategories] = useState<ICategory[]>()
    const [categoryId, setCategoryId] = useState<string>()
    const [allowedCategories, setAllowedCategories] = useState<ICategory[]>()
    const [displayFilters, setDisplayFilters] = useState<boolean>(false)
    const [conversionSources, setConversionSources] = useState<IConversionSource[]>([]);
    const [factorPreview, setFactorPreview] = useState<IRulePreview>()
    // Type value set by the edit loader, so the type-clear effect can tell a loaded
    // rule's type apart from a user toggling the Type dropdown.
    const hydratedTypeRef = useRef<string | null>(null)
    //Formik
    const ruleSchema = Yup.object().shape({
      value: Yup.string().required('You must provide a value'),
      name: Yup.string().required('You must provide a name'),
      type: Yup.string().required('You must provide a type'),
      conversionSourceId: Yup.string().when('type', {
        is: 'activity-based emission factor',
        then: schema => schema.required('You must select a conversion source'),
        otherwise: schema => schema.nullable(),
      }),
      groupName: Yup.string().when('type', {
        is: 'activity-based emission factor',
        then: schema => schema.required('You must provide a group'),
        otherwise: schema => schema.nullable(),
      }),
      categoryName: Yup.string().when('type', {
        is: 'activity-based emission factor',
        then: schema => schema.required('You must provide a category'),
        otherwise: schema => schema.nullable(),
      }),
      detailLevelOne: Yup.string().when('type', {
        is: 'activity-based emission factor',
        then: schema => schema.required('You must provide additional information'),
        otherwise: schema => schema.nullable(),
      }),
      detailLevelTwo: Yup.string().when('type', {
        is: 'activity-based emission factor',
        then: schema => schema.required('You must provide additional information'),
        otherwise: schema => schema.nullable(),
      }),
      detailLevelThree: Yup.string().nullable(),
      detailLevelFour: Yup.string().nullable(),
      detailLevelFive: Yup.string().nullable(),
      measurementUnitName: Yup.string().when('type', {
        is: 'activity-based emission factor',
        then: schema => schema.required('You must provide a measurement unit'),
        otherwise: schema => schema.nullable(),
      }),
      vehicleType: Yup.string().when('type', {
        is: 'vehicle',
        then: schema => schema.required('You must choose a vehicle type'),
        otherwise: schema => schema.nullable(),
      }),
      returnId: Yup.string().when('type', {
        // Required on create for activity rules only. Vehicle rules may set the vehicle
        // type alone and leave the conversion factor unset, so it is optional for them.
        // On edit the factor is already stored and the loader does not repopulate returnId,
        // so requiring it would make every activity-rule edit fail validation silently.
        is: (typeVal: string) => !isEdit && typeVal === 'activity-based emission factor',
        then: schema => schema.required('You must select a conversion factor'),
        otherwise: schema => schema.notRequired(),
      }),
    })
    const formik = useFormik<IPostRule>({
      initialValues: {
        value: '',
        name: '',
        type: 'activity-based emission factor',
        conversionSourceId: null,
        groupName: '',
        returnId: '',
        categoryName: '',
        detailLevelOne: '',
        detailLevelTwo: '',
        detailLevelThree: '',
        detailLevelFour: '',
        detailLevelFive: '',
        measurementUnitName: '',
        conversionId: '',
        vehicleType: '',
      },
      validationSchema: ruleSchema,
      onSubmit: (values) => {
        onSubmit(values, formik.resetForm, isEdit)
      }
    })
    //Use Effects
    useEffect(() => {
      if (ruleId) {
        setDisplayFilters(true)
        const loadRule = fetchRule ?? ((id: string) => ruleService.getOne(id))
        loadRule(ruleId)
          .then(response => {
            hydratedTypeRef.current = response.type
            formik.setFieldValue('value', response.value)
            formik.setFieldValue('name', response.name)
            formik.setFieldValue('type', response.type)
            formik.setFieldValue('conversionSourceId', response.conversionSourceId)
            formik.setFieldValue('groupName', response.groupName)
            formik.setFieldValue('categoryName', response.categoryName)
            formik.setFieldValue('detailLevelOne', response.detailLevelOne)
            formik.setFieldValue('detailLevelTwo', response.detailLevelTwo)
            formik.setFieldValue('detailLevelThree', response.detailLevelThree)
            formik.setFieldValue('detailLevelFour', response.detailLevelFour)
            formik.setFieldValue('detailLevelFive', response.detailLevelFive)
            formik.setFieldValue('measurementUnitName', response.measurementUnitName)
            formik.setFieldValue('vehicleType', response.vehicleType || '')
            formik.setFieldValue('conversionId', response.conversionId || '')
            setConversionSourceId(response.conversionSourceId)
            setRule(response)
          })
          .catch(err => {
            console.log(err);
          })
      }
    }, [])

    useEffect(() => {
      if (rule && categories) {
        const category = findCategoryByDisplayName(categories, rule.categoryName)
        if (category) {
          setCategoryId(category.id)
          // Legacy rules can store the friendlyName; converge on the canonical name so the
          // Category dropdown (whose options are names) displays it and re-saves resolve.
          if (category.name !== rule.categoryName) {
            formik.setFieldValue('categoryName', category.name)
          }
        }
      }
    }, [rule, categories]);

    useEffect(() => {
      conversionSourceService.getAll(true, 'conversion')
        .then(response => {
          setConversionSources(response)
        })
        .catch(err => console.log(err))

      categoryService.getAllCategories()
        .then(response => {
          setCategories(response)
        })
    }, [])

    useEffect(() => {
      if (conversionSourceId) {
        groupService.getGroupsWithCategories(false)
          .then(response => {
            setGroups(response)
            if (rule && formik.values?.groupName) {
              const group = response.find(group => group.title === formik.values?.groupName)
              if (group) {
                setGroupId(group.id)
                setAllowedCategories(group.categories)
              } else {
                setGroupId('')
                setAllowedCategories([])
                setCategoryId('')
              }
            } else {
              setGroupId('')
              setAllowedCategories([])
              setCategoryId('')
            }
          })
      }
    }, [conversionSourceId, rule])

    useEffect(() => {
      if (!displayFilters) {
        formik.setFieldValue('measurementUnitName', '');
        formik.setFieldValue('returnId', '');
      }
      if (categoryId) {
        setCategoryId(categoryId)
      }
    }, [categoryId]);

    // Clear type-specific fields when the USER changes the rule type. The edit loader
    // also sets formik type, and that change must not wipe the values loaded with it.
    useEffect(() => {
      const hydratedType = hydratedTypeRef.current
      hydratedTypeRef.current = null
      if (hydratedType === formik.values.type) {
        return
      }
      if (formik.values.type === 'vehicle') {
        formik.setFieldValue('groupName', '')
        formik.setFieldValue('categoryName', '')
        formik.setFieldValue('conversionSourceId', null)
        formik.setFieldValue('detailLevelOne', '')
        formik.setFieldValue('detailLevelTwo', '')
        formik.setFieldValue('detailLevelThree', '')
        formik.setFieldValue('detailLevelFour', '')
        formik.setFieldValue('detailLevelFive', '')
        formik.setFieldValue('measurementUnitName', '')
        formik.setFieldValue('returnId', '')
        // Toggling away cleared the stored default factor; toggling back must not
        // silently persist its loss on the next save.
        formik.setFieldValue('conversionId', rule?.conversionId || '')
        setConversionSourceId(undefined)
        setGroups(undefined)
        setGroupId(undefined)
        setCategoryId(undefined)
        setAllowedCategories(undefined)
      } else if (formik.values.type === 'activity-based emission factor') {
        formik.setFieldValue('vehicleType', '')
        formik.setFieldValue('conversionId', '')
      }
    }, [formik.values.type])

    // Worked example of the factor the rule would apply ("100 litres = 274.93 kgCO2e"), resolved by
    // the backend with the same matching the import uses. Shown while the rule is being built,
    // which is the cheapest moment to notice a factor in the wrong unit or order of magnitude.
    const previewKey = JSON.stringify([
      formik.values.type, formik.values.conversionSourceId, formik.values.groupName, formik.values.categoryName,
      formik.values.detailLevelOne, formik.values.detailLevelTwo, formik.values.detailLevelThree,
      formik.values.detailLevelFour, formik.values.detailLevelFive, formik.values.measurementUnitName,
      formik.values.returnId, formik.values.conversionId,
    ])
    const debouncedPreviewKey = useDebounce(previewKey, 400)
    useEffect(() => {
      const values = formik.values
      const ready = values.type === 'vehicle'
        ? Boolean(values.returnId || values.conversionId)
        : Boolean(values.conversionSourceId && values.groupName && values.categoryName && values.measurementUnitName)
      if (!ready) {
        setFactorPreview(undefined)
        return
      }
      let cancelled = false
      ruleService.preview(values)
        .then(response => { if (!cancelled) setFactorPreview(response) })
        .catch(() => { if (!cancelled) setFactorPreview(undefined) })
      return () => { cancelled = true }
    }, [debouncedPreviewKey])

    const factorPreviewLine = factorPreview && (
      <Box sx={{ mt: 1.5, mb: 0.5 }}>
        {factorPreview.factor
          ? <Typography variant='body2' color='text.secondary'>Example: {formatRuleFactorExample(factorPreview.factor)}</Typography>
          : <Typography variant='body2' color='error.main'>{factorPreview.message}</Typography>}
      </Box>
    )

    const conversionSourceHandler = (e: SelectChangeEvent) => {
      if (e.target.value !== formik.values.conversionSourceId) {
        formik.setFieldValue('conversionSourceId', e.target.value);
        setConversionSourceId(e.target.value)
        formik.setFieldValue('groupName', '')
        formik.setFieldValue('categoryName', '')
        setGroupId('')
        setCategoryId('')
      }
    }

    const selectGroupHandler = (e: SelectChangeEvent) => {
      const oldGroup = groups?.find(group => group.title === formik.values.groupName)
      const newGroup = groups?.find(group => group.title === e.target.value)
      if (oldGroup?.title !== newGroup?.title) {
        formik.setFieldValue('groupName', newGroup?.title);
        setGroupId(newGroup?.id)
        setAllowedCategories(newGroup?.categories)
        formik.setFieldValue('categoryName', '')
        setCategoryId('')
      }
    }

    const selectCategoryHandler = (value: string) => {
      setDisplayFilters(false)
      formik.setFieldValue('categoryName', value);
      setCategoryId(findCategoryByDisplayName(categories, value)?.id)
    }

    const cancelHandler = () => {
      formik.resetForm();
      setGroupId('');
      setAllowedCategories([]);
      setCategoryId('');
      closeHandler();
    };

    const isVehicleRule = formik.values.type === 'vehicle';

    // On edit, an untouched factor selection is already resolved server-side, but once the
    // user changes any part of it the async returnId lookup must confirm the new
    // combination resolves to a factor before it can be persisted.
    const factorSelectionChanged = rule
      ? FACTOR_SELECTION_FIELDS.some(field => (formik.values[field] || '') !== (rule[field] || ''))
      : false;

    const isSaveDisabled = isEdit
      ? isVehicleRule
        ? !formik.values.vehicleType
        : !formik.values.measurementUnitName || (factorSelectionChanged && !formik.values.returnId)
      : isVehicleRule
        ? !formik.values.vehicleType
        : !formik.values.returnId;

    return (
      <Dialog open={open} onClose={closeHandler} maxWidth='xs'>
        <Box sx={{ p: 4 }} component='form' onSubmit={formik.handleSubmit}>
          <DialogTitle sx={{ pt: 0, pl: 0, pr: 0 }}>
            {!isEdit && `Add ${titleNoun}`}
            {isEdit && `Edit ${titleNoun}`}
          </DialogTitle>
          <DialogContent sx={{ pt: 0, pl: 0, pr: 0 }}>
            <FormControl fullWidth sx={{ mt: 0.5 }}>
              <InputLabel>Name</InputLabel>
              <OutlinedInput id='name'
                name='name'
                label='Name'
                fullWidth
                value={formik.values.name || ''}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                sx={{ color: 'text.primary', mr: 2 }}></OutlinedInput>
              {formik.touched.name && formik.errors.name &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.name ? <div>{formik.errors.name as string}</div> : null}
                </FormHelperText>
              }
              {errorMessage && errorCode === 1 &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {errorMessage}
                </FormHelperText>
              }
            </FormControl>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='type'>Type</InputLabel>
              <Select
                id='type' label='Type' name='type'
                value={formik.values.type}
                onChange={formik.handleChange}
                error={formik.touched.type && Boolean(formik.errors.type)}
                sx={{ color: 'text.primary' }}>
                <MenuItem value={'activity-based emission factor'}>Activity-Based Conversion Factor</MenuItem>
                <MenuItem value={'vehicle'}>Vehicle Rule</MenuItem>
              </Select>
              {formik.touched.type && formik.errors.type &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.type as string}</FormHelperText>
              }
            </FormControl>

            {/* ── Emission Rule UI ── */}
            {!isVehicleRule && <>
              {conversionSources &&
                <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                  <InputLabel htmlFor='conversionSourceId'>Conversion Factor Source</InputLabel>
                  <Select
                    id='conversionSourceId' label='Conversion Factor Source' name='conversionSourceId'
                    value={formik.values.conversionSourceId || ''}
                    onChange={conversionSourceHandler}
                    error={formik.touched.conversionSourceId && Boolean(formik.errors.conversionSourceId)}
                    sx={{ color: 'text.primary' }}>
                    {conversionSources.sort((a, b) => a.source.localeCompare(b.source)).map((conversionSource, index) => (
                      <MenuItem key={index} value={conversionSource.id}>
                        <Stack direction='row' spacing={1} alignItems='center'>
                          <span>{conversionSource.source}</span>
                          {conversionSource.country && (
                            <img
                              loading="lazy"
                              width="20"
                              src={conversionSource.country.imageUrl}
                              alt={`${conversionSource.country.name} flag`}
                            />
                          )}
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.conversionSourceId && formik.errors.conversionSourceId &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.conversionSourceId as string}</FormHelperText>
                  }
                  {errorMessage && errorCode === 6 &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                      {errorMessage}
                    </FormHelperText>
                  }
                </FormControl>}
              <Stack direction='row' alignItems={'center'} sx={{ mt: 2, mb: 0.5 }}>
                <Typography variant='subtitle1'>Condition</Typography>
                <TooltipButton
                  text={'The value of the condition is the term that must match in the imported data to apply the rule'}
                  placement='top'
                />
              </Stack>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                When the imported data description is:
              </Typography>
              <FormControl fullWidth sx={{ mt: 0.5 }}>
                <InputLabel>Value</InputLabel>
                <OutlinedInput id='value'
                  name='value'
                  label='Value'
                  fullWidth
                  value={formik.values.value || ''}
                  onChange={formik.handleChange}
                  error={formik.touched.value && Boolean(formik.errors.value)}
                  sx={{ color: 'text.primary', mr: 2 }}></OutlinedInput>
                {formik.touched.value && formik.errors.value &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                    {formik.errors.value ? <div>{formik.errors.value as string}</div> : null}
                  </FormHelperText>
                }
                {errorMessage && errorCode === 2 &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                    {errorMessage}
                  </FormHelperText>
                }
              </FormControl>
              <Stack direction='row' alignItems={'center'} sx={{ mt: 2, mb: 0.5 }}>
                <Typography variant='subtitle1'>Action</Typography>
                <TooltipButton
                  text={'The classification that will be assigned when the condition is matched'}
                  placement='top'
                />
              </Stack>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                Assign emission category:
              </Typography>
              {groups &&
                (<FormControl fullWidth variant='outlined' sx={{ mt: 1 }}>
                  <InputLabel htmlFor='groupName'>Group</InputLabel>
                  <Select
                    id='groupName' label='Group' name='groupName'
                    value={formik.values.groupName || ''}
                    onChange={selectGroupHandler}
                    error={formik.touched.groupName && Boolean(formik.errors.groupName)}
                    sx={{ color: 'text.primary' }}>
                    {sortGroupsByGhgCategory(groups).map((group, index) =>
                      <MenuItem key={index} value={group.title}>{formatGroupLabel(group)}</MenuItem>
                    )}
                  </Select>
                  {formik.touched.groupName && formik.errors.groupName &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.groupName as string}</FormHelperText>
                  }
                  {errorMessage && errorCode === 4 &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                      {errorMessage}
                    </FormHelperText>
                  }
                </FormControl>)
              }
              {!groups &&
                (<FormControl fullWidth variant='outlined' sx={{ mt: 1 }}>
                  <InputLabel htmlFor='groupName'>Group</InputLabel>
                  <Select id='groupName' label='Group' name='groupName' disabled />
                </FormControl>)
              }
              {allowedCategories && allowedCategories.length > 0 && <FormControl fullWidth variant='outlined' sx={{ mt: 2, mb: 2 }}>
                <InputLabel htmlFor='categoryName'>Category</InputLabel>
                <Select
                  id='categoryName' label='Category' name='categoryName'
                  value={formik.values.categoryName || ''}
                  onChange={(e) => selectCategoryHandler(e.target.value)}
                  error={formik.touched.categoryName && Boolean(formik.errors.categoryName)}
                  sx={{ color: 'text.primary' }}>
                  {allowedCategories.map((category, index) =>
                    <MenuItem key={index} value={category.name}>{getCategoryDisplayName(category)}</MenuItem>
                  )}
                </Select>
                {formik.touched.categoryName && formik.errors.categoryName &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.categoryName as string}</FormHelperText>
                }
                {errorMessage && errorCode === 5 &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                    {errorMessage}
                  </FormHelperText>
                }
              </FormControl>}
              {categoryId && groupId && conversionSourceId &&
                <EmissionsFilterLogic
                  categoryId={categoryId}
                  groupId={groupId}
                  defaultConversionSourceId={conversionSourceId}
                  formik={formik}
                  isEdit={isEdit}
                  emptyStateMessage={NO_FACTORS_MESSAGE}
                  direction='column' />
              }
              {factorPreviewLine}
            </>}

            {/* ── Vehicle Rule UI ── */}
            {isVehicleRule && <>
              {conversionSources &&
                <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                  <InputLabel htmlFor='conversionSourceId'>Conversion Factor Source</InputLabel>
                  <Select
                    id='conversionSourceId' label='Conversion Factor Source' name='conversionSourceId'
                    value={formik.values.conversionSourceId || ''}
                    onChange={conversionSourceHandler}
                    error={formik.touched.conversionSourceId && Boolean(formik.errors.conversionSourceId)}
                    sx={{ color: 'text.primary' }}>
                    {conversionSources.sort((a, b) => a.source.localeCompare(b.source)).map((conversionSource, index) => (
                      <MenuItem key={index} value={conversionSource.id}>
                        <Stack direction='row' spacing={1} alignItems='center'>
                          <span>{conversionSource.source}</span>
                          {conversionSource.country && (
                            <img
                              loading="lazy"
                              width="20"
                              src={conversionSource.country.imageUrl}
                              alt={`${conversionSource.country.name} flag`}
                            />
                          )}
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>}
              <Stack direction='row' alignItems={'center'} sx={{ mt: 2, mb: 0.5 }}>
                <Typography variant='subtitle1'>Condition</Typography>
                <TooltipButton
                  text={'The rule name must match the value in the Rule column of the vehicle import file'}
                  placement='top'
                />
              </Stack>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                When the rule name in the import matches:
              </Typography>
              <FormControl fullWidth sx={{ mt: 0.5 }}>
                <InputLabel>Value</InputLabel>
                <OutlinedInput id='value'
                  name='value'
                  label='Value'
                  fullWidth
                  value={formik.values.value || ''}
                  onChange={formik.handleChange}
                  error={formik.touched.value && Boolean(formik.errors.value)}
                  sx={{ color: 'text.primary', mr: 2 }}></OutlinedInput>
                {formik.touched.value && formik.errors.value &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                    {formik.errors.value ? <div>{formik.errors.value as string}</div> : null}
                  </FormHelperText>
                }
                {errorMessage && errorCode === 2 &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                    {errorMessage}
                  </FormHelperText>
                }
              </FormControl>
              <Stack direction='row' alignItems={'center'} sx={{ mt: 2, mb: 0.5 }}>
                <Typography variant='subtitle1'>Action</Typography>
                <TooltipButton
                  text={'The vehicle type and conversion factor that will be applied when the rule matches'}
                  placement='top'
                />
              </Stack>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                Set vehicle type as:
              </Typography>
              <FormControl fullWidth variant='outlined' sx={{ mt: 0.5 }}>
                <InputLabel htmlFor='vehicleType'>Vehicle Type</InputLabel>
                <Select
                  id='vehicleType' label='Vehicle Type' name='vehicleType'
                  value={formik.values.vehicleType || ''}
                  onChange={formik.handleChange}
                  error={formik.touched.vehicleType && Boolean(formik.errors.vehicleType)}
                  sx={{ color: 'text.primary' }}>
                  {VEHICLE_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
                {formik.touched.vehicleType && formik.errors.vehicleType &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.vehicleType as string}</FormHelperText>
                }
                {errorMessage && errorCode === 10 &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                    {errorMessage}
                  </FormHelperText>
                }
              </FormControl>
              {groups &&
                (<FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                  <InputLabel htmlFor='groupNameVehicle'>Group</InputLabel>
                  <Select
                    id='groupNameVehicle' label='Group' name='groupName'
                    value={formik.values.groupName || ''}
                    onChange={selectGroupHandler}
                    sx={{ color: 'text.primary' }}>
                    {sortGroupsByGhgCategory(groups).map((group, index) =>
                      <MenuItem key={index} value={group.title}>{formatGroupLabel(group)}</MenuItem>
                    )}
                  </Select>
                </FormControl>)
              }
              {!groups && conversionSourceId &&
                (<FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                  <InputLabel htmlFor='groupNameVehicle'>Group</InputLabel>
                  <Select id='groupNameVehicle' label='Group' name='groupName' disabled />
                </FormControl>)
              }
              {allowedCategories && allowedCategories.length > 0 && <FormControl fullWidth variant='outlined' sx={{ mt: 2, mb: 2 }}>
                <InputLabel htmlFor='categoryNameVehicle'>Category</InputLabel>
                <Select
                  id='categoryNameVehicle' label='Category' name='categoryName'
                  value={formik.values.categoryName || ''}
                  onChange={(e) => selectCategoryHandler(e.target.value)}
                  sx={{ color: 'text.primary' }}>
                  {allowedCategories.map((category, index) =>
                    <MenuItem key={index} value={category.name}>{getCategoryDisplayName(category)}</MenuItem>
                  )}
                </Select>
              </FormControl>}
              <Typography variant='body2' color='text.secondary' sx={{ mt: allowedCategories && allowedCategories.length > 0 ? 0 : 1, mb: 0.5 }}>
                Set default conversion factor (optional):
              </Typography>
              {categoryId && groupId && conversionSourceId &&
                <EmissionsFilterLogic
                  categoryId={categoryId}
                  groupId={groupId}
                  defaultConversionSourceId={conversionSourceId}
                  formik={formik}
                  isEdit={isEdit}
                  emptyStateMessage={NO_FACTORS_MESSAGE}
                  // The default conversion here is optional and there is no blank option to
                  // clear it, so a single-option level must stay a deliberate blank rather
                  // than acquiring a factor the user never chose.
                  autoSelectSingleOptions={false}
                  direction='column' />
              }
              {factorPreviewLine}
              {errorMessage && errorCode === 11 &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {errorMessage}
                </FormHelperText>
              }
            </>}
          </DialogContent>
          <DialogActions sx={{ p: 0 }}>
            <DialogSaveButtons
              cancelHandler={cancelHandler}
              submitHandler={formik.submitForm}
              disableSaveButton={isSaveDisabled}
              isSubmitting={isSubmitting}
            />
          </DialogActions>
        </Box>
      </Dialog >
    )
  }
